"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getRenderPixel = getRenderPixel;
exports.getVectorContext = getVectorContext;
exports.toContext = toContext;

var _Immediate = _interopRequireDefault(require("./render/canvas/Immediate.js"));

var _has = require("./has.js");

var _transform = require("./transform.js");

var _vector = require("./renderer/vector.js");

var _proj = require("./proj.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/render
 */

/**
 * @typedef {Object} State
 * @property {CanvasRenderingContext2D} context Canvas context that the layer is being rendered to.
 * @property {import("./Feature.js").FeatureLike} feature Feature.
 * @property {import("./geom/SimpleGeometry.js").default} geometry Geometry.
 * @property {number} pixelRatio Pixel ratio used by the layer renderer.
 * @property {number} resolution Resolution that the render batch was created and optimized for.
 * This is not the view's resolution that is being rendered.
 * @property {number} rotation Rotation of the rendered layer in radians.
 */

/**
 * A function to be used when sorting features before rendering.
 * It takes two instances of {@link module:ol/Feature~Feature} or
 * {@link module:ol/render/Feature~RenderFeature} and returns a `{number}`.
 *
 * @typedef {function(import("./Feature.js").FeatureLike, import("./Feature.js").FeatureLike):number} OrderFunction
 */

/**
 * @typedef {Object} ToContextOptions
 * @property {import("./size.js").Size} [size] Desired size of the canvas in css
 * pixels. When provided, both canvas and css size will be set according to the
 * `pixelRatio`. If not provided, the current canvas and css sizes will not be
 * altered.
 * @property {number} [pixelRatio=window.devicePixelRatio] Pixel ratio (canvas
 * pixel to css pixel ratio) for the canvas.
 */

/**
 * Binds a Canvas Immediate API to a canvas context, to allow drawing geometries
 * to the context's canvas.
 *
 * The units for geometry coordinates are css pixels relative to the top left
 * corner of the canvas element.
 * ```js
 * import {toContext} from 'ol/render';
 * import Fill from 'ol/style/Fill';
 * import Polygon from 'ol/geom/Polygon';
 *
 * var canvas = document.createElement('canvas');
 * var render = toContext(canvas.getContext('2d'),
 *     { size: [100, 100] });
 * render.setFillStrokeStyle(new Fill({ color: blue }));
 * render.drawPolygon(
 *     new Polygon([[[0, 0], [100, 100], [100, 0], [0, 0]]]));
 * ```
 *
 * @param {CanvasRenderingContext2D} context Canvas context.
 * @param {ToContextOptions} [opt_options] Options.
 * @return {CanvasImmediateRenderer} Canvas Immediate.
 * @api
 */
function toContext(context, opt_options) {
  var canvas = context.canvas;
  var options = opt_options ? opt_options : {};
  var pixelRatio = options.pixelRatio || _has.DEVICE_PIXEL_RATIO;
  var size = options.size;

  if (size) {
    canvas.width = size[0] * pixelRatio;
    canvas.height = size[1] * pixelRatio;
    canvas.style.width = size[0] + 'px';
    canvas.style.height = size[1] + 'px';
  }

  var extent = [0, 0, canvas.width, canvas.height];
  var transform = (0, _transform.scale)((0, _transform.create)(), pixelRatio, pixelRatio);
  return new _Immediate["default"](context, pixelRatio, extent, transform, 0);
}
/**
 * Gets a vector context for drawing to the event's canvas.
 * @param {import("./render/Event.js").default} event Render event.
 * @return {CanvasImmediateRenderer} Vector context.
 * @api
 */


function getVectorContext(event) {
  if (!(event.context instanceof CanvasRenderingContext2D)) {
    throw new Error('Only works for render events from Canvas 2D layers');
  } // canvas may be at a different pixel ratio than frameState.pixelRatio


  var canvasPixelRatio = event.inversePixelTransform[0];
  var frameState = event.frameState;
  var transform = (0, _transform.multiply)(event.inversePixelTransform.slice(), frameState.coordinateToPixelTransform);
  var squaredTolerance = (0, _vector.getSquaredTolerance)(frameState.viewState.resolution, canvasPixelRatio);
  var userTransform;
  var userProjection = (0, _proj.getUserProjection)();

  if (userProjection) {
    userTransform = (0, _proj.getTransformFromProjections)(userProjection, frameState.viewState.projection);
  }

  return new _Immediate["default"](event.context, canvasPixelRatio, frameState.extent, transform, frameState.viewState.rotation, squaredTolerance, userTransform);
}
/**
 * Gets the pixel of the event's canvas context from the map viewport's CSS pixel.
 * @param {import("./render/Event.js").default} event Render event.
 * @param {import("./pixel.js").Pixel} pixel CSS pixel relative to the top-left
 * corner of the map viewport.
 * @return {import("./pixel.js").Pixel} Pixel on the event's canvas context.
 * @api
 */


function getRenderPixel(event, pixel) {
  return (0, _transform.apply)(event.inversePixelTransform, pixel.slice(0));
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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